import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import CloseIcon from "../components/icons/CloseIcon";
import ProductArtworkPreview from "./ProductArtworkPreview";
import { useModalMotion } from "../hooks/useModalMotion";
import {
  normalizeMediaUrl,
  productName,
  resolveProductDisplayImage,
} from "./productService";
import {
  findAvailableVariant,
  getAvailableStock,
  getRemainingStock,
  isColorAvailableForSize,
  isProductInStock,
  shouldSkipStockCheck,
} from "./stockService";
import {
  formatPrice,
  getColorLabel,
  getSizeLabel,
  uniqueByLabel,
  productUnitPrice,
  sortSizes,
} from "./shopUtils";
import { preloadImages } from "./ProductArtworkPreview";

function CartGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 25 25" fill="none" aria-hidden="true">
      <path
        d="M21.5 16.5H7.5a1 1 0 0 1 0-2h10.44a2.5 2.5 0 0 0 2.41-1.77L22.5 5.74a1 1 0 0 0-.18-.86 1 1 0 0 0-.82-.38H7.26A2.5 2.5 0 0 0 4.44 2.5H3.5a1 1 0 0 0 0 2h.94a.5.5 0 0 1 .5.5l.06.48 1.73 6.76A3 3 0 0 0 7.5 18.5h.18a3 3 0 1 0 5.64 0h2.36a3 3 0 1 0 5.64 0H21.5a1 1 0 0 0 0-2ZM20.22 6.5l-1.31 5.24a.5.5 0 0 1-.5.26H9.28L7.78 6.5h12.44ZM10.5 20.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm8 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
        fill="currentColor"
      />
    </svg>
  );
}

function colorHasOwnImage(color) {
  if (!color || typeof color !== "object") return false;
  return Boolean(
    color.productImageUrl ||
    color.product_image_url ||
    color.picture ||
    color.image ||
    color.imgURL ||
    color.imageUrl
  );
}

function resolveColorImage(color, product) {
  if (!color || typeof color !== "object") return resolveProductDisplayImage(product);
  const hint = product?.picture || product?.imgURL;
  return (
    normalizeMediaUrl(color.productImageUrl, hint) ||
    normalizeMediaUrl(color.product_image_url, hint) ||
    normalizeMediaUrl(color.picture, hint) ||
    normalizeMediaUrl(color.image, hint) ||
    normalizeMediaUrl(color.imgURL, hint) ||
    normalizeMediaUrl(color.imageUrl, hint) ||
    resolveProductDisplayImage(product)
  );
}

function isSizeSelectable(product, size, color, cartItems = [], skipStock = false) {
  if (skipStock) return true;
  return (
    getRemainingStock(product, { size, color }, cartItems) > 0
  );
}

function pickInitialVariant(product, sizes, colors, cartItems = []) {
  const available = findAvailableVariant(product);
  if (!available) {
    return {
      color: colors[0] || null,
      size: sizes[0] || null,
    };
  }

  const colorLabel = getColorLabel(available.color).toLowerCase();
  const sizeLabel = getSizeLabel(available.size).toLowerCase();
  const color =
    colors.find(
      (item) => getColorLabel(item).toLowerCase() === colorLabel
    ) ||
    available.color ||
    colors[0] ||
    null;

  let size =
    sizes.find((item) => getSizeLabel(item).toLowerCase() === sizeLabel) ||
    available.size ||
    null;

  if (size && !isSizeSelectable(product, size, color, cartItems)) {
    size =
      sizes.find((item) =>
        isSizeSelectable(product, item, color, cartItems)
      ) || null;
  }
  if (!size) {
    size =
      sizes.find((item) =>
        isSizeSelectable(product, item, color, cartItems)
      ) ||
      sizes[0] ||
      null;
  }

  return { color, size };
}

function ProductModal({
  product,
  artworkUrl,
  quantity = 1,
  cartItems = [],
  onClose,
  onConfirm,
}) {
  const sizes = sortSizes(
    uniqueByLabel(
      Array.isArray(product?.product_sizes) ? product.product_sizes : [],
      getSizeLabel
    )
  );
  const colors = uniqueByLabel(
    Array.isArray(product?.color) ? product.color : [],
    getColorLabel,
    (current, next) => !colorHasOwnImage(current) && colorHasOwnImage(next)
  );
  const showSizes = sizes.length > 1;
  const showColors = colors.length > 1;
  const skipStock = shouldSkipStockCheck(product);

  const [color, setColor] = useState(
    () => pickInitialVariant(product, sizes, colors, cartItems).color
  );
  const [size, setSize] = useState(
    () => pickInitialVariant(product, sizes, colors, cartItems).size
  );
  const [qty, setQty] = useState(() => Math.max(1, quantity || 1));
  const { requestClose, motionClassName } = useModalMotion(onClose);

  useEffect(() => {
    const next = pickInitialVariant(product, sizes, colors, cartItems);
    setColor(next.color);
    setSize(next.size);
    setQty(Math.max(1, quantity || 1));
  }, [product]);

  // If selected size is sold out for the current color, move to an available size
  // (or switch color when this color has no sizes left).
  useEffect(() => {
    if (skipStock) return;
    if (
      size &&
      isSizeSelectable(product, size, color, cartItems, skipStock)
    ) {
      return;
    }
    const nextSize = sizes.find((item) =>
      isSizeSelectable(product, item, color, cartItems, skipStock)
    );
    if (nextSize) {
      setSize(nextSize);
      return;
    }
    const next = pickInitialVariant(product, sizes, colors, cartItems);
    const sameColor =
      getColorLabel(next.color).toLowerCase() ===
      getColorLabel(color).toLowerCase();
    const sameSize =
      getSizeLabel(next.size).toLowerCase() ===
      getSizeLabel(size).toLowerCase();
    if (sameColor && sameSize) return;
    setColor(next.color);
    setSize(next.size);
  }, [cartItems, color, colors, product, size, sizes, skipStock]);

  useEffect(() => {
    preloadImages([
      resolveProductDisplayImage(product),
      ...colors.map((item) => resolveColorImage(item, product)),
    ]);
  }, [product]);

  const productImage = useMemo(
    () => resolveColorImage(color, product),
    [color, product]
  );

  const variantStock = useMemo(
    () =>
      skipStock
        ? Number.MAX_SAFE_INTEGER
        : getAvailableStock(product, { size, color }),
    [color, product, size, skipStock]
  );
  const remaining = useMemo(
    () =>
      skipStock
        ? Number.MAX_SAFE_INTEGER
        : getRemainingStock(product, { size, color }, cartItems),
    [cartItems, color, product, size, skipStock]
  );
  const variantInStock = skipStock || remaining > 0;
  const canIncrement = remaining > 0 && qty < remaining;

  useEffect(() => {
    if (skipStock) return;
    if (remaining <= 0) {
      setQty(0);
      return;
    }
    setQty((current) => Math.min(Math.max(current || 1, 1), remaining));
  }, [remaining, skipStock, size, color]);

  if (!product) return null;
  const name = productName(product);
  const unit = productUnitPrice(product, size);
  const productSoldOut = !skipStock && !isProductInStock(product);

  const handleConfirm = () => {
    if (!variantInStock || qty < 1) {
      toast.error("This item is sold out");
      return;
    }
    if (!skipStock && qty > remaining) {
      toast.error(`Only ${remaining} in stock`);
      return;
    }
    onConfirm({ quantity: qty, size, color });
  };

  return (
    <div
      className={`shopModal${motionClassName}`}
      role="dialog"
      aria-modal="true"
      onClick={requestClose}
    >
      <div className="shopModal__card" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="shopModal__close"
          onClick={requestClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
        <div className="shopModal__layout">
          <div className="shopModal__media">
            <ProductArtworkPreview
              productImage={productImage}
              artworkImage={artworkUrl}
              productName={name}
              skuCode={product.sku_code}
              placement={
                product.artwork_placement || product.artworkPlacement || product.print_area
              }
            />
          </div>
          <div className="shopModal__detail">
            <h2 className="shopModal__title">{name}</h2>

            {showSizes ? (
              <div className="shopModal__section">
                <p className="shopModal__label">Size</p>
                <div className="shopModal__options">
                  {sizes.map((item) => {
                    const label = getSizeLabel(item);
                    const sizeInStock = isSizeSelectable(
                      product,
                      item,
                      color,
                      cartItems,
                      skipStock
                    );
                    const selected =
                      sizeInStock &&
                      getSizeLabel(size).toLowerCase() === label.toLowerCase();
                    return (
                      <button
                        key={label}
                        type="button"
                        className={`shopModal__chip${selected ? " is-on" : ""}${
                          sizeInStock ? "" : " is-soldOut"
                        }`}
                        disabled={!sizeInStock}
                        aria-disabled={!sizeInStock}
                        onClick={() => {
                          if (!sizeInStock) return;
                          setSize(item);
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {showColors ? (
              <div className="shopModal__section">
                <p className="shopModal__label">Color</p>
                <div className="shopModal__options">
                  {colors.map((item) => {
                    const label = getColorLabel(item);
                    const selected =
                      getColorLabel(color).toLowerCase() === label.toLowerCase();
                    const colorInStock =
                      skipStock || isColorAvailableForSize(product, item, size);
                    return (
                      <button
                        key={label}
                        type="button"
                        className={`shopModal__chip${selected ? " is-on" : ""}${
                          colorInStock ? "" : " is-soldOut"
                        }`}
                        aria-disabled={!colorInStock}
                        onClick={() => {
                          if (!colorInStock) return;
                          setColor(item);
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div className="shopModal__section">
              <p className="shopModal__label">Quantity</p>
              <div className="shopModal__qty">
                <button
                  type="button"
                  onClick={() =>
                    setQty((current) => Math.max(variantInStock ? 1 : 0, current - 1))
                  }
                  aria-label="Decrease"
                  disabled={qty <= 1}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 12L6 12" stroke="#33363F" strokeWidth="2" strokeLinecap="square" strokeLinejoin="round" />
                  </svg>
                </button>
                <span>{qty}</span>
                <button
                  type="button"
                  onClick={() =>
                    setQty((current) =>
                      canIncrement ? current + 1 : current
                    )
                  }
                  aria-label="Increase"
                  disabled={!canIncrement}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 6L12 18" stroke="#33363F" strokeWidth="2" strokeLinecap="square" strokeLinejoin="round" />
                    <path d="M18 12L6 12" stroke="#33363F" strokeWidth="2" strokeLinecap="square" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              {!skipStock ? (
                <p
                  className={`shopModal__stock${
                    variantInStock ? " shopModal__stock--ok" : " shopModal__stock--sold"
                  }`}
                >
                  {variantInStock
                    ? remaining < Number.MAX_SAFE_INTEGER
                      ? `In stock: ${remaining} left`
                      : "In stock"
                    : "Sold out"}
                </p>
              ) : null}
            </div>

            <div className="shopModal__section mt-3">
              <p className="shopModal__label">Price</p>
              <p className="shopModal__price">
                {formatPrice(unit * Math.max(qty, 0))}
              </p>
            </div>

            <div className="shopModal__actions">
              <button
                type="button"
                className="shopModal__cancel"
                onClick={requestClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="shopModal__add"
                disabled={!variantInStock || productSoldOut || qty < 1}
                onClick={handleConfirm}
              >
                <CartGlyph />
                {variantInStock ? "Add to cart" : "Sold out"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductModal;
